import {Component, OnInit, ViewChild} from '@angular/core';
import {DatabaseCollectionProvider, DatabaseService, SyncSettings} from '@core/database';
import {SideMenu} from './menu/side-menu';
import {FeaturedEventsManagerService} from 'calendar/services/featured-events-manager.service';
import {DayEvaluationEventService} from '@library/plugin-day-evaluation/src/lib/services/day-evaluation-event.service';
import {AppSettings} from '@plugin/settings';
import {AlertController, LoadingController, NavController, Platform} from '@ionic/angular';
import {DeviceNotificationService} from '@plugin/device-notifications';
import {NotificationSchedulerService} from './notifications/notification-scheduler.service';
import {CalendarEventInterceptor} from './notifications/listeners/calendar-event.interceptor';
import {SurveyEventService} from 'survey/services/survey-event.service';
import {DayStatisticService} from '@plugin/statistics';
import {SetupService} from './setup.service';
import {BarcodeScanner} from 'capacitor-barcode-scanner';
import {Subscription, timer} from 'rxjs';
import {environment} from '../environments/environment';
import {PinLockService} from './pin-lock/pin-lock.service';
import {StatsSummaryService} from './stats-summary/stats-summary.service';
import * as moment from 'moment';
import {AppGlobals} from './app.global';
import {SplashScreen} from '@ionic-native/splash-screen/ngx';
import {ThemeService} from './theme.service';
import {DeviceService} from './device.service';
import {CloudConnectService} from '@plugin/cloud-connect';

import * as Sentry from 'sentry-cordova';
import {Severity} from 'sentry-cordova';
import {LoggingService} from './logging.service';
import {MatDrawer} from '@angular/material/sidenav';
import {DataUpdateService} from '../updates/data-update.service';
import {UpdateService} from './update.service';
import { DATABASE_COLLECTIONS } from '@library/core-models/src/schema';
import { LifeSectionModel } from '@library/core-models';
import {JobService} from './job-runner/job.service';
import { TrialService } from './trial.service';
import { LifeSectionService } from '@library/plugin-life-section/src/public_api';
import { PrivacyPolicyService } from './privacy-policy/privacy-policy.service';
import { AppointmentsService } from './appointments.service';
import { Router } from '@angular/router';
import { NotificationUpdaterService } from './notification-settings/notification-updater.service';
import { SideMenuService } from './menu/side-menu.service';
import { ProductVersionService } from './product-version.service';

if (environment.enableSentryMonitoring) {
    Sentry.init({ release: environment.version, dsn: 'https://5b1d8297bcc04d418f3bed47ef75b4b3@sentry.solutec.de/48' });
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    @ViewChild('drawer', { static: true }) drawer: MatDrawer;

    appPages: Array<any>;
    view: 'app' | 'login' | 'pin' | 'privacy-policy';
    ready: boolean;
    value: any;
    mode: string;
    syncSettings: SyncSettings;
    disableSync: boolean;
    connecting: boolean;
    trialExpired = false;
    settingsSub: Subscription;
    private lifeSections: LifeSectionModel[] = [];

    constructor(
        private featureManager: FeaturedEventsManagerService,
        private themeService: ThemeService,
        private dayEvaluationEvent: DayEvaluationEventService,
        private surveyEvent: SurveyEventService,
        private navController: NavController,
        private notificationService: DeviceNotificationService,
        private notificationScheduler: NotificationSchedulerService,
        private calendarEventInterceptor: CalendarEventInterceptor,
        private jobService: JobService,
        private dayStatsService: DayStatisticService,
        private databaseService: DatabaseService,
        private settings: AppSettings,
        private appGlobals: AppGlobals,
        private sideMenuService: SideMenuService,
        private alertService: AlertController,
        private db: DatabaseCollectionProvider,
        private platform: Platform,
        private pinLockService: PinLockService,
        private loadingController: LoadingController,
        private statsSummaryService: StatsSummaryService,
        private setupService: SetupService,
        private splashScreen: SplashScreen,
        private deviceService: DeviceService,
        private connectService: CloudConnectService,
        private updateService: UpdateService,
        private dataUpdateService: DataUpdateService,
        private log: LoggingService,
        private trialService: TrialService,
        private lifeSectionService: LifeSectionService,
        private privacyPolicyService: PrivacyPolicyService,
        private appointmentsService: AppointmentsService,
        private notificationUpdater: NotificationUpdaterService,
        private router: Router,
        private productVersionService: ProductVersionService,
    ) {}

    gotToSettings() {
        this.navController.navigateForward('/settings');
    }

    cloudConnect() {
        this.navController.navigateForward('/connect');
    }

    async boot() {

        this.appGlobals.setDateToUse(Date.now());

        await this.databaseService.databaseAwaiter();
        let syncSettings = await this.db.getSyncSettings();
        if (!environment.disableSync && !syncSettings) {
          this.view = 'login';
            await this.settings.awaiter();
            if (this.settings.has('trial-expired') && this.settings.get('trial-expired') === true) {
              this.trialExpired = true;
            }
            try {
                this.splashScreen.hide();
            } catch (e) { console.warn('no cordova environment') }
            return;
        }

        if (!await this.setupService.hasDefaultData())
            await this.setupService.createDefaultData();

        await this.notificationService.setup();
        this.notificationScheduler.register(this.calendarEventInterceptor);
        await this.notificationScheduler.start();

        // checks if lifesection.enableNotifications has changed and adjusts the local notifications
        this.lifeSections = await this.lifeSectionService.getAllOnce();
        this.lifeSectionService.observeChanges().subscribe(async (newLifeSections) => {
          let shouldUpdateNotifications = false;
          for(const lifesection of this.lifeSections) {
            const index = newLifeSections.findIndex(x => x.id === lifesection.id);
            if (index === -1 || lifesection.enableNotifications !== newLifeSections[index].enableNotifications) {
              shouldUpdateNotifications = true;
              break;
            }
          }
          if (shouldUpdateNotifications) {
            this.notificationScheduler.stop();
            this.notificationScheduler.register(this.calendarEventInterceptor);
            await this.notificationScheduler.start();
          }
        });

        this.dayEvaluationEvent.activate(this.settings, this.db, this.dayStatsService);
        this.featureManager.addProvider(this.dayEvaluationEvent);

        this.surveyEvent.activate(this.settings, this.db);
        this.featureManager.addProvider(this.surveyEvent);

        try {
            this.splashScreen.hide();
        } catch (e) { console.warn('no cordova environment') }

        this.view = 'pin';
        await this.pinLockService.waitForPin();

        if (!navigator.onLine) {
            this.checkForPatientTermination();
        }

        this.view = 'app';
        await this.statsSummaryService.update();
        this.checkForDataUpdates();

        if(this.settings.get('version') === null) {
            const os = this.platform.is('android') ? '-android' : this.platform.is('ios') ? '-ios' : '-gen';
            this.settings.set('version', environment.version + os);
        }

        if (this.settings.get('init-timestamp') === null) {
            this.settings.set('init-timestamp', moment().unix());
        }
        console.log('before fetch')
        await this.settings.fetchRemote();


        console.log('running pending jobs')
        await this.jobService.runAllPending();

        console.log('after fetch');
        await this.databaseService.pullData([DATABASE_COLLECTIONS.SettingsCollection]);

        this.databaseService.startLiveDBReplication().then(async () => {
            console.log('sync finished, setting last-data-sync date');
            this.settings.set('last-data-sync', moment().toISOString());

            this.checkForPatientTermination();
            await this.checkForAppointments();
            this.updateSettingsSub();
        });

         // uncomment this to enable the alert popup for the sociodemographic data
        // await this.formService.check();

        this.notificationUpdater.run();

        console.log('before stats summary')
        this.statsSummaryService.startUpdater();

        if (this.settings.has('trial-start') && this.settings.get('trial-expired') !== true) {
					this.trialService.startTrialTimeInterval();

					const notificationIds = this.settings.get('trial-notifications');

					if (!notificationIds || !notificationIds.length) {
						this.trialService.createTrialExpirationNotifications();
					}
        }

        setTimeout(async () => {
            await this.updateService.checkForUpdates(true);
        }, 5000);
    }

    checkForPatientTermination() {
        const terminationDate = this.settings.has('termination-date') ? this.settings.get('termination-date') : false;
        const continuedAfterTermination = this.settings.has('after-termination-connect-date') ? this.settings.get('after-termination-connect-date') : false;
        if (terminationDate && !continuedAfterTermination) {
            this.router.navigate(['termination-page']);
        }
    }

    async getLoader() {
        return await this.loadingController.create({
            id: 'create-entries',
            animated: true,
            message: 'Daten werden synchronisiert'
        });
    }

    async checkForAppointments() {
        await this.settings.loadFromDatabase();
        const appointments = this.settings.get('appointments', []);
        if (appointments.length) {
            this.appointmentsService.handle(appointments);
        }
    }

    updateSettingsSub() {
        if (this.settingsSub) {
            this.settingsSub.unsubscribe();
        }
        if (this.databaseService.settingsStream) {
            this.settingsSub = this.databaseService.settingsStream.docs$.subscribe(async (doc) => {
                if (doc.key === 'appointments') {
                    this.appointmentsService.handle(doc.value);
                }

                if (doc.key === 'termination-date') {
                    if (doc.value) {
                        this.settings.set('termination-date', doc.value);
                        this.router.navigate(['termination-page']);
                    }
                }
            });
        }
    }

    checkForDataUpdates() {
        setTimeout(async () => {
            if (await this.dataUpdateService.checkForUpdates()) {
                await this.dataUpdateService.installUpdate();
                await this.settings.loadFromDatabase();
            }
        }, 5000)
    }

    async onClickConnect(trial: boolean = false) {
        if (!navigator.onLine) {
            this.displayNoInternetErrorMsg();
            return;
        }

        if (!this.privacyPolicyService.hasAcceptedPrivacyPolicy()) {
            const previousView = this.view
            this.view = 'privacy-policy';
            await this.privacyPolicyService.waitToAcceptPrivacyPolicy();
            this.view = previousView;
        }

        if(!trial) {
          this.settings.set('trial-expired', true);
        }

        this.connect()
            .then(x => {
                this.connecting = false;
                window.location.href = '/notification-settings';
            })
            .catch(async e => {
                console.log('catch', e);
                const AUTH_ERROR_ALREADY_IN_USE = 0x01;
                if (e.params && e.params.code === AUTH_ERROR_ALREADY_IN_USE) {
                    const dialog = await this.alertService.create({
                        header: 'Zugangscode wurde bereits verwendet',
                        message: 'Setze Dich mit Deinem Coach in Verbindung, um einen neuen zu erhalten.',
                        buttons: [{ text: 'Okay', role: 'cancel' }]
                    });
                    dialog.present();
                }
                this.connecting = false;
            });
    }

    scanCode() {
        BarcodeScanner
            .scan()
            .then(({ code }) => {
                this.value = code;
                this.onClickConnect();
            });
    }

    async connect() {
        if (this.connecting)
            return;

        this.connecting = true;

        this.log.write(`connecting new user ${this.value}`, Severity.Info);

        if (!this.privacyPolicyService.hasAcceptedPrivacyPolicy()) {
            const previousView = this.view
            this.view = 'privacy-policy';
            await this.privacyPolicyService.waitToAcceptPrivacyPolicy();
            this.view = previousView;
        }

        let done = false;
        return new Promise(async (resolve, reject) => {
            const loader = await this.getLoader();
            loader.present();

            await this.deviceService.complete();

            this.connectService.connect(this.value, this.deviceService.toJSON()).subscribe(
                async step => {
                    switch (step.stage) {
                        case 'init':
                        case 'connect':
                        case 'auth':
                            console.log('step', step);
                            break;
                        case 'already-connecting':
                            resolve(null);
                            break;
                        case 'finished':
                            console.log('step', step);
                            try {
                                let delayer = timer(2000).toPromise();
                                this.syncSettings = step.params.settings;
                                this.settings.set('study-number', step.params.settings.studyNumber);
                                this.settings.set('internalId', step.params.settings.internalId);
                                await this.db.saveSyncSettings(this.syncSettings);
                                await this.databaseService.syncNow();
                                await delayer;
                                await loader.dismiss();
                                done = true;
                                resolve(this.syncSettings);
                            } catch (e) {
                                await loader.dismiss();
                                console.error('connection error', e);
                                reject(e)
                                location.reload()
                            }
                            break;
                    }
                },
                async error => {
                    Sentry.captureException(error.originalError || error);
                    console.error('connection error', error);
                    if (!done)
                        await loader.dismiss();
                    reject(error);
                }
            )
        })

    }

    ngOnInit() {
        this.disableSync = environment.disableSync;
        this.view = 'app';
        this.platform.ready().then(async name => {
            try {
                await this.boot();
                console.log('boot done')
            } catch (e) {
                console.error(e);
            }

            await this.databaseService.databaseAwaiter();
            this.ready = true;
            this.mode = name;
            this.syncSettings = await this.db.getSyncSettings();
            await this.productVersionService.updateProductVersion();
            this.appPages = this.sideMenuService.getMenu();

            if (!this.privacyPolicyService.hasAcceptedPrivacyPolicy()) {
                const previousView = this.view
                this.view = 'privacy-policy';
                await this.privacyPolicyService.waitToAcceptPrivacyPolicy();
                this.view = previousView;
            }

            if (this.platform.is('cordova')) {
                this.platform.resume.subscribe(async () => {
                    await this.databaseService.databaseAwaiter();
                    await this.settings.awaiter();

                    if (!navigator.onLine) {
                        this.checkForPatientTermination()
                    }

                    await this.databaseService.pullData([DATABASE_COLLECTIONS.SettingsCollection]);
                    if (this.databaseService.settingsStream) {
                        this.databaseService.settingsStream.cancel();
                    }

                    this.databaseService.startLiveDBReplication([DATABASE_COLLECTIONS.SettingsCollection]).then(async () => {
                        await this.settings.loadFromDatabase();
                        this.checkForPatientTermination();
                        await this.checkForAppointments();
                        this.updateSettingsSub();
                    });

                    this.notificationUpdater.run();
                    await this.statsSummaryService.update()
                    await this.updateService.checkForUpdates(true);
                    this.checkForDataUpdates();
                    this.settings.set('last-data-sync', moment().toISOString());
                });
            }
        })
    }

    async connectToTrial() {
        await this.settings.awaiter();
        if (this.settings.get('trial-expired') === true) {
            location.reload();
            return;
        }
        const token = await this.trialService.getTrialToken();
        this.value = token;
        this.onClickConnect(true);
        this.settings.set('feature-depression-chart', true);
        this.settings.set('feature-conflicts', true);
        this.settings.set('feature-resolutions', true);
        this.settings.set('trial-start', moment().toISOString());
        this.trialService.startTrialTimeInterval();
    }

    async displayNoInternetErrorMsg() {
        const dialog = await this.alertService.create({
            message: 'Es konnte keine Internetverbindung hergestellt werden.',
            buttons: [
                {
                    text: 'Okay',
                    role: 'cancel'
                }
            ]
        });
        dialog.present();
    }
}
